/**
 * Lokální sběrnice událostí pro triggery vrstev.
 *
 * ZÁMĚRNĚ nezávisí na trackeru: tracker bez souhlasu s analytikou eventy
 * zahazuje, ale trigger musí fungovat dál (zobrazení vrstvy není tracking —
 * viz privacy). Emituje se z `cart.tsx` (add_to_cart, hodnota košíku),
 * z page-view efektu a z produktové stránky. Kdo chce trigger poslat i do
 * CDP, zavolá vedle toho `track()` — jedno místo, dva odběratelé.
 */

export type LayerBusEvent =
  | { readonly type: 'add_to_cart' }
  | { readonly type: 'cart_value'; readonly valueMinor: number }
  | { readonly type: 'page_view'; readonly path: string }
  | { readonly type: 'product_view'; readonly productId: string }
  | { readonly type: 'custom'; readonly name: string };

type Listener = (event: LayerBusEvent) => void;

const listeners = new Set<Listener>();

/** Poslední známá hodnota košíku – nový odběratel (trigger `cart_value`)
 *  ji potřebuje hned, ne až při příští změně. */
let lastCartValueMinor = 0;

export function onLayerEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(event: LayerBusEvent): void {
  if (event.type === 'cart_value') lastCartValueMinor = event.valueMinor;
  // Kopie do pole: odběratel se smí odhlásit uprostřed doručování.
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch {
      // Odběratel triggeru nesmí shodit ani košík, ani stránku.
    }
  }
}

/** Košík přidal položku (hranová událost). */
export function emitAddToCart(): void {
  emit({ type: 'add_to_cart' });
}

/** Košík změnil hodnotu (stavová událost, v halířích). */
export function emitCartValue(valueMinor: number): void {
  emit({ type: 'cart_value', valueMinor });
}

export function emitPageView(path: string): void {
  emit({ type: 'page_view', path });
}

export function emitProductView(productId: string): void {
  emit({ type: 'product_view', productId });
}

/** Vlastní pojmenovaná událost (trigger `custom_event`). */
export function emitLayerEvent(name: string): void {
  emit({ type: 'custom', name });
}

export function currentCartValueMinor(): number {
  return lastCartValueMinor;
}
